import React, { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { PageHeader } from '../components/PageHeader'
import { CheckCircleIcon, ArrowRightIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline'

interface DeleteSuccessState {
  type: 'lesson' | 'student' | 'expense'
  itemName: string
  deletedAt: string
  returnPath: string
}

export const DeleteSuccessPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as DeleteSuccessState

  // If no state provided, redirect back
  useEffect(() => {
    if (!state?.type) {
      navigate('/admin/dashboard', { replace: true })
    }
  }, [state, navigate])

  if (!state?.type) {
    return null // Will redirect in useEffect
  }

  const formatThaiDateTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getTypeInfo = () => {
    const types = {
      lesson: {
        title: 'ลบบทเรียนสำเร็จ',
        itemType: 'บทเรียน',
        successMessage: 'บทเรียนถูกลบออกจากระบบแล้ว',
        impactMessage: 'ผู้ปกครองจะไม่สามารถดูบทเรียนนี้ได้อีก',
        nextSteps: [
          'ตรวจสอบว่าบทเรียนอื่นๆ ยังครบถ้วนหรือไม่',
          'พิจารณาโพสต์บทเรียนทดแทน (ถ้าจำเป็น)',
          'แจ้งผู้ปกครองหากมีการเปลี่ยนแปลงสำคัญ'
        ]
      },
      student: {
        title: 'ลบข้อมูลนักเรียนสำเร็จ',
        itemType: 'ข้อมูลนักเรียน',
        successMessage: 'ข้อมูลนักเรียนถูกลบออกจากระบบแล้ว',
        impactMessage: 'ผู้ปกครองจะไม่สามารถเข้าใช้ระบบได้อีก',
        nextSteps: [
          'ตรวจสอบรายชื่อนักเรียนที่เหลือ',
          'ปรับปรุงข้อมูลการเรียนการสอน',
          'จัดการข้อมูลการเงินที่เหลือ (ถ้ามี)'
        ]
      },
      expense: {
        title: 'ลบรายการค่าใช้จ่ายสำเร็จ',
        itemType: 'รายการค่าใช้จ่าย',
        successMessage: 'รายการค่าใช้จ่ายถูกลบออกจากระบบแล้ว',
        impactMessage: 'ข้อมูลการเรียกเก็บเงินจะไม่ปรากฏอีก',
        nextSteps: [
          'ตรวจสอบรายการค่าใช้จ่ายอื่นๆ',
          'อัพเดตข้อมูลการเงิน',
          'แจ้งผู้ปกครองหากจำเป็น'
        ]
      }
    }
    return types[state.type]
  }

  const typeInfo = getTypeInfo()

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader 
        title={typeInfo.title}
        showBackButton={false}
      />
      
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircleIcon className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            ลบข้อมูลสำเร็จ
          </h1>
          <p className="text-gray-600">
            {typeInfo.successMessage}
          </p>
        </div>

        {/* Deletion Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">รายละเอียดการลบ</h2>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">ประเภท:</span>
              <span className="font-medium">{typeInfo.itemType}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-600">รายการที่ลบ:</span>
              <span className="font-medium">{state.itemName}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-600">เวลาที่ลบ:</span>
              <span className="font-medium">{formatThaiDateTime(state.deletedAt)}</span>
            </div>
          </div>
        </div>

        {/* Impact Information */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-yellow-800 mb-2">📋 ผลกระทบ</h3>
          <p className="text-sm text-yellow-700">
            {typeInfo.impactMessage}
          </p>
        </div>

        {/* Next Steps */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
          <h3 className="font-medium text-blue-900 mb-3">🎯 สิ่งที่ควรทำต่อไป</h3>
          <div className="space-y-2 text-sm text-blue-800">
            {typeInfo.nextSteps.map((step, index) => (
              <div key={index} className="flex items-start">
                <span className="w-6 h-6 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
                  {index + 1}
                </span>
                <span>{step}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={() => navigate(state.returnPath)}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
          >
            <ClipboardDocumentListIcon className="h-5 w-5 mr-2" />
            {state.type === 'lesson' ? 'กลับไปรายการบทเรียน' :
             state.type === 'student' ? 'กลับไปรายการนักเรียน' :
             'กลับไปรายการค่าใช้จ่าย'}
          </button>
          
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center justify-center"
          >
            <ArrowRightIcon className="h-5 w-5 mr-2 rotate-180" />
            กลับไปหน้าหลัก
          </button>
        </div>

        {/* Recovery Note */}
        <div className="mt-8 bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="font-medium text-red-800 mb-2">⚠️ ข้อควรระวัง</h3>
          <p className="text-sm text-red-700">
            ข้อมูลที่ลบไปแล้วไม่สามารถกู้คืนได้ หากต้องการข้อมูลเดิม 
            จะต้องเพิ่มใหม่ทั้งหมด
          </p>
        </div>

        {/* Support Information */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            หากมีปัญหาหรือข้อสงสัย กรุณาติดต่อผู้ดูแลระบบ
          </p>
        </div>
      </div>
    </div>
  )
}